/*
This module contains functions that we bind to the window to setup slots
and control the display of ads.
These functions are called by the views that load the ads in Rails,
usually from inside an intermediate 'command array' that will execute after
the google library is loaded.
*/
export const createWindowFunctionsForAdSetup = () => {
  window.googletag = window.googletag || { cmd: [] };
  window.carwowGoogleTag = window.carwowGoogleTag || [];
  window.carwowGoogleAdSlots = window.carwowGoogleAdSlots || [];

  window.defineGoogleAdSlot = defineGoogleAdSlot
  window.displayGoogleAd = displayGoogleAd
}

const defineGoogleAdSlot = (googleAdJson) => {
  const googleAd = JSON.parse(googleAdJson);

  window.googletag.cmd.push(() => {
    const slot = (googleAd.native) ? buildNativeAdSlot(googleAd) : buildStandardAdSlot(googleAd)

    addBlueconicTargetingValues(slot)

    // Run our first-party targeting values last, so that it takes precedence over any duplicate keys
    // from the third party targeting (via blueconic)
    for (const [key, value] of Object.entries(googleAd.targetingKeyValues)) {
      slot.setTargeting(key, value);
    }

    if (new RegExp("(?:^|;\\s*)carwow_admin_token\\s*\\=").test(document.cookie)) {
      slot.setTargeting('carwow_admin', true);
    }
  });
}

const buildNativeAdSlot = ({ unitPath, divId }) => {
  const slot = window.googletag.defineSlot(unitPath, ['fluid'], divId)

  slot.addService(window.googletag.pubads())

  return slot
}

const buildStandardAdSlot = (googleAd) => {
  const slot = window.googletag.defineSlot(googleAd.unitPath, googleAd.allDeviceSizes, googleAd.divId)

  const mapping = window.googletag.sizeMapping()
        .addSize([1167, 0], googleAd.largeDesktopSizes)
        .addSize([1025, 0], googleAd.desktopSizes)
        .addSize([791, 0], googleAd.largeTabletSizes)
        .addSize([651, 0], googleAd.tabletSizes)
        .addSize([0, 0], googleAd.mobileSizes)
        .build();

  slot.defineSizeMapping(mapping);

  slot.addService(window.googletag.pubads())

  return slot
}

const addBlueconicTargetingValues = (slot) => {
  // Blueconic docs say that these values might be (very slightly) outdated
  // if BC hasn't yet updated the user's attributes.
  // See https://support.blueconic.com/hc/en-us/articles/115004519689-Google-Ad-Manager-for-Publishers-Targeting-Only-Connection#adjust-your-google-ad-manager-implementation-code-0-1
  const targetingParams = JSON.parse(localStorage.getItem("bcDFPTargetingParams"))

  if (targetingParams) {
    targetingParams.forEach(({ key, value }) => {
      slot.setTargeting(key, value)
    })
  }
}

// This function is useful to remove any old ads when re-rendering parts of the page
// ie in Turbo Frames where the new response does not contain the previously rendered ad element
const removeAnyAdsNoLongerInTheDOM = () => {
  const slotsToBeRemoved = googletag.pubads().getSlots().filter(slot => document.getElementById(slot.getSlotElementId()) === null)
  if (slotsToBeRemoved.length) {
    googletag.destroySlots(slotsToBeRemoved)
  }
}

const displayGoogleAd = (elementId, display) => {
  window.googletag.cmd.push(function() {
    removeAnyAdsNoLongerInTheDOM();
    // Gotcha: if we call disableInitialLoad() when initializing the service
    // this only *registers* the slot rather than displaying it
    googletag.display(elementId);

    if (display) {
      const slot = googletag.pubads().getSlots().find(slot => slot.getSlotElementId() === elementId);
      // to display the ad we need to call pubads().refresh(slot)
      // but we pass it into this array because we need to ensure the user has interacted
      // with GDPR consent before this happens
      window.carwowGoogleAdSlots.push(slot)
    } else {
      // If display was false, we want to hide this element, perhaps as part of an AB-test or
      // because it is loaded and controlled later.
      document.getElementById(elementId).style.display = 'none';
    }
  });
}
